import { useEffect } from 'react';
import { updateSEO, getSiteUrl } from '../../utils/seo';

export default function RecruitPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    
    const siteUrl = getSiteUrl();
    
    updateSEO({
      title: '採用情報 | おかやま介護認定調査株式会社 - 介護認定調査員募集',
      description: 'おかやま介護認定調査株式会社の採用情報。介護認定調査員を募集中。介護支援専門員、社会福祉士、介護福祉士の資格をお持ちの方、地域社会に貢献しませんか。完全週休2日制、社会保険完備、資格取得支援制度あり。',
      keywords: '採用情報,求人,介護認定調査員,介護支援専門員,社会福祉士,介護福祉士,岡山市,正社員募集',
      canonical: `${siteUrl}/recruit`,
      ogType: 'website',
      schema: {
        '@context': 'https://schema.org',
        '@type': 'JobPosting',
        '@id': `${siteUrl}/recruit#jobposting`,
        title: '介護認定調査員',
        description: '岡山市を中心に介護認定調査を専門に行う当社で、一緒に働く仲間を募集しています。介護支援専門員、社会福祉士、介護福祉士のいずれかの資格をお持ちの方を歓迎します。',
        datePosted: '2026-01-01',
        validThrough: '2026-12-31',
        employmentType: 'FULL_TIME',
        hiringOrganization: {
          '@type': 'Organization',
          name: 'おかやま介護認定調査株式会社',
          sameAs: siteUrl,
          logo: `${siteUrl}/logo.png`
        },
        jobLocation: {
          '@type': 'Place',
          address: {
            '@type': 'PostalAddress',
            streetAddress: '○○町1-2-3',
            addressLocality: '岡山市北区',
            addressRegion: '岡山県',
            postalCode: '700-0000',
            addressCountry: 'JP'
          }
        },
        baseSalary: {
          '@type': 'MonetaryAmount',
          currency: 'JPY',
          value: {
            '@type': 'QuantitativeValue',
            minValue: 250000,
            maxValue: 350000,
            unitText: 'MONTH'
          }
        },
        qualifications: '介護支援専門員、社会福祉士、介護福祉士のいずれかの資格、普通自動車免許（AT限定可）',
        responsibilities: '介護認定調査業務、訪問調査、調査票作成、報告書作成',
        skills: '介護認定調査の知識、コミュニケーション能力、調査スキル',
        workHours: '9:00-18:00',
        benefits: '社会保険完備、交通費全額支給、資格取得支援制度、定期健康診断、社員旅行'
      }
    });
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 sm:py-24 lg:py-32 bg-gradient-to-br from-[#F5F1E8] to-[#E8F4F4]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full mb-6 shadow-sm">
            <div className="w-2 h-2 bg-[#5D9B9B] rounded-full"></div>
            <span className="text-sm font-medium text-[#2C3E50]">RECRUIT</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#2C3E50] mb-6">
            採用情報
          </h1>
          <p className="text-lg sm:text-xl text-gray-700">
            一緒に働く仲間を募集しています
          </p>
        </div>
      </section>

      {/* Ideal Candidate Section */}
      <section className="py-16 sm:py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2C3E50] text-center mb-16">
            求める人物像
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-heart-line',
                title: '思いやりの心',
                description: '介護を必要とされる方々に寄り添い、一人ひとりの状況を丁寧に理解しようとする姿勢を大切にしています。',
                color: 'bg-[#FFE8E8]'
              },
              {
                icon: 'ri-team-line',
                title: '協調性',
                description: 'チームワークを大切にし、スタッフ同士で協力しながら質の高いサービスを提供できる方を求めています。',
                color: 'bg-[#E8E8F4]'
              },
              {
                icon: 'ri-book-open-line',
                title: '向上心',
                description: '常に学び続け、専門知識やスキルを向上させようとする意欲のある方を歓迎します。',
                color: 'bg-[#E8F4E8]'
              }
            ].map((item, index) => (
              <div
                key={index}
                className={`${item.color} rounded-2xl p-8 hover:shadow-xl transition-all duration-300 relative overflow-hidden group cursor-pointer`}
              >
                <div className="absolute top-6 right-6 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <i className="ri-arrow-right-up-line text-[#2C3E50]"></i>
                </div>
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mb-6 shadow-md">
                  <i className={`${item.icon} text-3xl text-[#2C3E50]`}></i>
                </div>
                <h3 className="text-2xl font-bold text-[#2C3E50] mb-4">
                  {item.title}
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Work Environment Section */}
      <section className="py-16 sm:py-20 lg:py-24 bg-[#F5F1E8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#2C3E50] mb-6">
                働きやすい環境づくり
              </h2>
              <p className="text-gray-700 leading-relaxed mb-8">
                私たちは、スタッフが安心して長く働ける環境づくりに力を入れています。ワークライフバランスを大切にし、充実した福利厚生を整えています。
              </p>
              <ul className="space-y-4">
                {[
                  '完全週休2日制（日曜・祝日）',
                  '社会保険完備',
                  '交通費全額支給',
                  '資格取得支援制度',
                  '定期的な研修制度',
                  '有給休暇取得推奨',
                  '育児・介護休暇制度',
                  '社員旅行・懇親会'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-checkbox-circle-fill text-[#5D9B9B] text-xl"></i>
                    </div>
                    <span className="text-gray-700 font-medium">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img
                src="https://public.readdy.ai/ai/img_res/05a3f8c126f1f1f7dd3be9d9588d2dd4.jpg"
                alt="働きやすいオフィス環境"
                className="w-full h-auto object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Staff Interview Section */}
      <section className="py-16 sm:py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2C3E50] text-center mb-16">
            スタッフの声
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                name: '田中 花子',
                position: '介護支援専門員',
                years: '入社3年目',
                comment: '一人ひとりの利用者様と向き合える環境が魅力です。チームのサポートもあり、安心して調査業務に取り組めています。',
                image: 'https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/e0bc0314a44ef3599727f3c099f68121.jpeg'
              },
              {
                name: '佐藤 健太',
                position: '介護支援専門員',
                years: '入社5年目',
                comment: '研修制度が充実しており、常にスキルアップできる環境です。地域に貢献できる仕事にやりがいを感じています。',
                image: 'https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/31d3ec9923a4110c96ace83897810bc1.png'
              },
              {
                name: '山本 美咲',
                position: '社会福祉士',
                years: '入社2年目',
                comment: 'ワークライフバランスが取りやすく、プライベートも充実しています。先輩方のサポートも手厚く、成長できる職場です。',
                image: 'https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/b17eb3be058b55ff393bbf0f3a97cfb4.png'
              }
            ].map((staff, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                <div className="w-24 h-24 mx-auto mb-4 rounded-full overflow-hidden">
                  <img
                    src={staff.image}
                    alt={staff.name}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="text-center mb-4">
                  <h3 className="text-xl font-bold text-[#2C3E50] mb-1">
                    {staff.name}
                  </h3>
                  <p className="text-sm text-gray-600">{staff.position}</p>
                  <p className="text-xs text-[#5D9B9B] font-medium">{staff.years}</p>
                </div>
                <div className="relative">
                  <i className="ri-double-quotes-l text-4xl text-[#5D9B9B]/20 absolute -top-2 -left-2"></i>
                  <p className="text-gray-700 leading-relaxed text-sm relative z-10 pl-4">
                    {staff.comment}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Requirements Section */}
      <section id="requirements" className="py-16 sm:py-20 lg:py-24 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2C3E50] text-center mb-12">
            募集要項
          </h2>

          <div className="bg-white rounded-2xl shadow-lg p-8 sm:p-12">
            <div className="space-y-6">
              {[
                { label: '職種', value: '介護認定調査員' },
                { label: '雇用形態', value: '正社員' },
                { label: '給与', value: '月給 250,000円〜350,000円（経験・能力により優遇）' },
                { label: '勤務時間', value: '9:00〜18:00（休憩1時間）' },
                { label: '休日', value: '完全週休2日制（日曜・祝日）、年末年始、夏季休暇、有給休暇' },
                { label: '応募資格', value: '介護支援専門員、社会福祉士、介護福祉士のいずれかの資格をお持ちの方\n普通自動車免許（AT限定可）' },
                { label: '福利厚生', value: '社会保険完備、交通費全額支給、資格取得支援制度、定期健康診断、社員旅行' },
                { label: '応募方法', value: 'お電話またはお問い合わせフォームからご連絡ください。\n履歴書・職務経歴書をご持参の上、面接にお越しください。' }
              ].map((item, index) => (
                <div
                  key={index}
                  className="grid grid-cols-1 sm:grid-cols-3 gap-4 pb-6 border-b border-gray-200 last:border-b-0"
                >
                  <div className="text-sm font-medium text-gray-500">
                    {item.label}
                  </div>
                  <div className="sm:col-span-2 text-base font-medium text-[#2C3E50] whitespace-pre-line">
                    {item.value}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 lg:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2C3E50] mb-6">
            まずはお気軽にご相談ください
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 mb-10">
            応募に関するご質問やご相談も承ります
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:086-012-3456"
              className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#2C3E50] text-white rounded-lg font-medium hover:bg-[#1a252f] transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill text-xl"></i>
              <span className="text-lg">086-012-3456</span>
            </a>
            <a
              href="/contact"
              className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#5D9B9B] text-white rounded-lg font-medium hover:bg-[#4a7d7d] transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              <span className="text-lg">応募・お問い合わせ</span>
              <i className="ri-arrow-right-line text-xl"></i>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
